// src/components/pages/admin-dash/EditUserModal.jsx
import React, { useEffect, useState } from "react";
import Button from "../../ui/Button";
import Field from "../../ui/Field";
import Icon from "../../ui/Icon";

/**
 * Edit-user modal.
 *
 * Changes from the old version:
 *   - Themed via design tokens (works in dark mode).
 *   - Audit "note" field — the admin must give a reason when changing a
 *     user's status, role, or credit balance. Posted alongside the user
 *     update so the backend can record an audit log entry. (Backend
 *     contract suggested: { id, email, active, type, account: { credit },
 *     audit: { note, fields: ["active", "credit", ...] } }.)
 *   - Inline "delta" preview so the admin sees what's actually changing.
 *   - Inline validation (email shape, credit ≥ 0).
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function EditUserModal({ user, onClose, onSave }) {
  const initial = {
    email:  user.email  || "",
    active: user.active || "TRUE",
    type:   user.type   || "USER",
    credit: user.account?.credit ?? 0,
  };

  const [form, setForm]       = useState(initial);
  const [note, setNote]       = useState("");
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    setForm(initial);
    setNote("");
    setTouched(false);
    setError("");
    setSuccess("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !loading && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [loading, onClose]);

  const setField = (name) => (e) => {
    setTouched(true);
    setForm((f) => ({ ...f, [name]: e.target.value }));
  };

  const changedFields = [];
  if (form.email  !== initial.email)             changedFields.push("email");
  if (form.active !== initial.active)            changedFields.push("active");
  if (form.type   !== initial.type)              changedFields.push("type");
  if (Number(form.credit) !== Number(initial.credit)) changedFields.push("credit");

  const sensitiveChange = changedFields.some((k) =>
    ["active", "type", "credit"].includes(k)
  );

  // Validation
  const emailOk  = EMAIL_RE.test(form.email);
  const creditOk = Number.isFinite(Number(form.credit)) && Number(form.credit) >= 0;
  const noteOk   = !sensitiveChange || note.trim().length > 0;
  const formOk   = emailOk && creditOk && noteOk && changedFields.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formOk) {
      if (!noteOk) setError("Add an audit note before saving sensitive changes.");
      return;
    }
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      await onSave({
        id: user.id,
        email:  form.email,
        active: form.active,
        type:   form.type,
        account: { credit: Number(form.credit) },
        // The backend can ignore this if it doesn't yet log audit notes.
        audit: { note: note.trim(), fields: changedFields },
      });
      setSuccess("User updated successfully.");
      setTimeout(onClose, 900);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to update user.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[150] flex items-center justify-center px-4 py-8"
      style={{ background: "rgba(0,0,0,0.45)", backdropFilter: "blur(2px)" }}
      onClick={() => !loading && onClose?.()}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg fade-in-up max-h-[90vh] overflow-y-auto"
        style={{
          background: "var(--surf)",
          border: "1px solid var(--border)",
          borderRadius: "var(--r-xl)",
          boxShadow: "var(--shadow-lg)",
        }}
      >
        {/* Header */}
        <div
          className="flex items-start gap-3 p-6"
          style={{ borderBottom: "1px solid var(--border)" }}
        >
          <div
            className="shrink-0 inline-flex items-center justify-center rounded-xl"
            style={{
              width: 40, height: 40,
              background: "var(--gold-dim)",
              color: "var(--gold)",
              border: "1px solid var(--gold-border)",
            }}
          >
            <Icon name="pencil" size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <h2
              className="font-semibold leading-tight"
              style={{ fontSize: 18, color: "var(--txt)" }}
            >
              Edit user
            </h2>
            <div
              className="text-[12px] mt-0.5 truncate"
              style={{ color: "var(--txt-2)" }}
            >
              {user.name} · #{user.id}
            </div>
          </div>
          <button
            type="button"
            disabled={loading}
            onClick={onClose}
            className="shrink-0 p-1.5 rounded-md transition-colors"
            style={{ color: "var(--txt-2)" }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.currentTarget.style.background = "var(--surf-2)";
                e.currentTarget.style.color      = "var(--red)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color      = "var(--txt-2)";
            }}
            aria-label="Close"
          >
            <Icon name="x" size={18} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-1">
          <Field
            label="Email"
            type="email"
            value={form.email}
            onChange={setField("email")}
            error={touched && !emailOk ? "Enter a valid email address." : undefined}
            required
          />

          <SelectFieldLabel
            label="Status"
            value={form.active}
            onChange={setField("active")}
            options={[
              { value: "TRUE",  label: "Active"   },
              { value: "FALSE", label: "Inactive — user can't log in" },
            ]}
          />

          <SelectFieldLabel
            label="Role"
            value={form.type}
            onChange={setField("type")}
            options={[
              { value: "USER",  label: "User"  },
              { value: "ADMIN", label: "Admin — full back-office access" },
            ]}
          />

          <Field
            label="Credit balance"
            type="number"
            min={0}
            value={form.credit}
            onChange={setField("credit")}
            error={touched && !creditOk ? "Credits must be 0 or greater." : undefined}
            hint={
              Number(form.credit) !== Number(initial.credit)
                ? `Δ ${
                    Number(form.credit) > Number(initial.credit) ? "+" : ""
                  }${Number(form.credit) - Number(initial.credit)} from current`
                : undefined
            }
          />

          {/* Audit note — required for sensitive changes */}
          {(sensitiveChange || note) && (
            <div className="mb-3.5">
              <label
                className="block text-[12px] font-medium mb-1.5 tracking-wide"
                style={{ color: "var(--txt)" }}
              >
                Audit note{" "}
                {sensitiveChange && (
                  <span style={{ color: "var(--gold)" }}>·  required</span>
                )}
              </label>
              <textarea
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Why are you making this change?"
                className="w-full px-3.5 py-[11px] text-[13px] outline-none rounded-2xl border resize-y"
                style={{
                  background: "var(--surf-2)",
                  color: "var(--txt)",
                  borderColor: "var(--border-2)",
                }}
              />
              <p className="mt-1.5 text-[11px]" style={{ color: "var(--txt-3)" }}>
                Recorded against your admin account for audit logs.
              </p>
            </div>
          )}

          {/* Change summary */}
          {changedFields.length > 0 && (
            <div
              className="rounded-xl px-3 py-2 mt-2 mb-1 text-[12px]"
              style={{
                background: "var(--gold-dim)",
                border: "1px solid var(--gold-border)",
                color: "var(--txt-2)",
              }}
            >
              <span style={{ color: "var(--txt)", fontWeight: 500 }}>
                Will update:
              </span>{" "}
              {changedFields.join(", ")}
            </div>
          )}

          {error && (
            <p className="text-[12px]" style={{ color: "var(--red)" }}>
              {error}
            </p>
          )}
          {success && (
            <p className="text-[12px]" style={{ color: "var(--gold)" }}>
              {success}
            </p>
          )}
        </form>

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-2 px-6 py-4"
          style={{ borderTop: "1px solid var(--border)" }}
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={loading}
            type="button"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            loading={loading}
            disabled={!formOk || loading}
            type="submit"
          >
            Save changes
          </Button>
        </div>
      </div>
    </div>
  );
}

function SelectFieldLabel({ label, value, onChange, options }) {
  return (
    <div className="mb-3.5">
      <label
        className="block text-[12px] font-medium mb-1.5 tracking-wide"
        style={{ color: "var(--txt)" }}
      >
        {label}
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={onChange}
          className="w-full px-3.5 py-[11px] text-[14px] outline-none rounded-full border appearance-none cursor-pointer pr-9"
          style={{
            background: "var(--surf-2)",
            color: "var(--txt)",
            borderColor: "var(--border-2)",
          }}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <span
          className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-[10px]"
          style={{ color: "var(--txt-3)" }}
        >
          ▾
        </span>
      </div>
    </div>
  );
}
